import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { MobileHeaderProvider } from './context/MobileHeaderContext';
import AddCustomerModal from '../components/AddCustomerModal';
import AddJobModal from '../components/AddJobModal';
import AddAppointmentModal from '../components/AddAppointmentModal';
import EditAppointmentModal from '../components/EditAppointmentModal';
import TreatmentReportEditor from '../components/TreatmentReportEditor';
import CustomerSearchModal from '../components/CustomerSearchModal';
import QuickBooksCallback from '../components/QuickBooksCallback';
import QuickBooksOAuthResult from '../components/QuickBooksOAuthResult';
import ErrorBoundary from '../components/ErrorBoundary';


import { useAppAuth } from './hooks/useAppAuth';
import { useAppData } from './hooks/useAppData';
import { useAppNavigation } from './hooks/useAppNavigation';
import { useAppModals } from './hooks/useAppModals';
import { useAppCRUD } from './hooks/useAppCRUD';

import LoadingScreen from './components/LoadingScreen';
import AuthGuard from './components/AuthGuard';
import AppRouter from './components/AppRouter';
import { isOAuthRoute, getFranchiseeIdForCRUD, getFranchiseeIdForDataLoading } from './services/appHelpers';
import { VIEWS } from './utils/appConstants';
import customerService from '../services/customerService';
import reminderService from '../services/reminderService';

// Import error reporting tests for development
if (process.env.NODE_ENV === 'development') {
  import('../utils/testErrorReporting');
}

function App() {
  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  // Initialize hooks
  const auth = useAppAuth();
  const appData = useAppData(auth.user);
  const navigation = useAppNavigation();
  const modalActions = useAppModals();
  const crudActions = useAppCRUD(auth.user, appData, navigation, modalActions);

  // State for handling customer creation from treatment report
  const [treatmentReportCustomerCallback, setTreatmentReportCustomerCallback] = useState(null);
  // State for handling customer creation from job flow (via customer search modal)
  const [jobCustomerCallback, setJobCustomerCallback] = useState(false);

  // Overdue reminders count for sidebar badge
  const [overdueRemindersCount, setOverdueRemindersCount] = useState(0);

  useEffect(() => {
    if (!auth.user || !auth.userProfile) return;
    const franchiseeId = getFranchiseeIdForDataLoading(auth.user);
    if (!franchiseeId) return;

    const fetchCount = async () => {
      try {
        const counts = await reminderService.getReminderCounts(franchiseeId);
        setOverdueRemindersCount(counts.overdue || 0);
      } catch (e) {
        // Silently fail — badge is informational
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [auth.user, auth.userProfile]);

  // Handle treatment report save
  const handleTreatmentReportSave = (savedReport) => {
    modalActions.closeTreatmentReportEditor();
    // Navigate back to treatment reports list
    navigation.handleNavigation(VIEWS.TREATMENT_REPORTS);
  };

  // Handle opening add customer modal from treatment report
  const handleOpenAddCustomerFromTreatmentReport = (customerSelectCallback) => {
    setTreatmentReportCustomerCallback(() => customerSelectCallback);
    modalActions.openAddCustomerModal();
  };

  // Handle customer creation from treatment report
  const handleAddCustomerFromTreatmentReport = async (customerData) => {
    if (!auth.user) return;

    try {
      const franchiseeId = customerData.franchisee_id || getFranchiseeIdForCRUD(auth.user);
      const response = await customerService.createCustomer(franchiseeId, customerData);
      modalActions.closeAddCustomerModal();
      await appData.refreshData(); // Refresh data after adding
      
      // If there's a callback (from treatment report), call it with the new customer
      if (treatmentReportCustomerCallback && response.customer) {
        treatmentReportCustomerCallback(response.customer);
        setTreatmentReportCustomerCallback(null);
      }
    } catch (error) {
      console.error('Error adding customer:', error);
    }
  };

  // Handle customer selection from search modal (for Add Job flow)
  const handleCustomerSearchSelect = (customer) => {
    modalActions.closeCustomerSearchModal();
    modalActions.setPreSelectedCustomer(customer);
    modalActions.openAddJobModal();
  };

  // Handle "Add New" from customer search modal
  const handleCustomerSearchAddNew = () => {
    modalActions.closeCustomerSearchModal();
    setJobCustomerCallback(true);
    modalActions.openAddCustomerModal();
  };

  // Handle customer creation when coming from job flow
  const handleAddCustomerFromJobFlow = async (customerData) => {
    if (!auth.user) return;

    try {
      const franchiseeId = customerData.franchisee_id || getFranchiseeIdForCRUD(auth.user);
      const response = await customerService.createCustomer(franchiseeId, customerData);
      modalActions.closeAddCustomerModal();
      await appData.refreshData();

      if (response.customer) {
        modalActions.setPreSelectedCustomer(response.customer);
        modalActions.openAddJobModal();
      }
      setJobCustomerCallback(false);
    } catch (error) {
      console.error('Error adding customer from job flow:', error);
    }
  };

  // Clear application state on logout
  const handleLogout = async () => {
    await auth.handleLogout();
    appData.setCustomers([]);
    appData.setJobs([]);
    navigation.clearSelections();
    navigation.setCurrentView(VIEWS.DASHBOARD);
  };

  // Check if this is an OAuth callback route
  const currentPath = window.location.pathname;
  if (isOAuthRoute(currentPath)) {
    if (currentPath === '/quickbooks/callback') {
      return <QuickBooksCallback />;
    }
    if (currentPath === '/qb/done') {
      return <QuickBooksOAuthResult />;
    }
  }

  // Show loading screen
  if (auth.loading || (auth.user && !auth.userProfile)) {
    return <LoadingScreen />;
  }

  return (
    <AuthGuard user={auth.user} onLogin={auth.handleLogin}>
      <ErrorBoundary>
        <MobileHeaderProvider>
        <div className="min-h-screen bg-gray-100 flex">
          {/* Mobile Header - only visible on mobile */}
          <MobileHeader
            onMenuClick={() => setIsMobileSidebarOpen(true)}
            currentView={navigation.currentView}
            subtitle={navigation.currentView === 'customer-details' ? navigation.selectedCustomer?.name : null}
          />
          
          {/* Sidebar - responsive */}
          <Sidebar 
            currentView={navigation.currentView} 
            onNavigate={(view) => {
              navigation.handleNavigation(view);
              setIsMobileSidebarOpen(false); // Close mobile sidebar on navigation
            }}
            onLogout={handleLogout}
            userEmail={auth.user?.email}
            userProfile={auth.userProfile}
            isMobileOpen={isMobileSidebarOpen}
            onMobileClose={() => setIsMobileSidebarOpen(false)}
            overdueRemindersCount={overdueRemindersCount}
          />
          
          {/* Mobile backdrop */}
          {isMobileSidebarOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
              onClick={() => setIsMobileSidebarOpen(false)}
            />
          )}
          
          <main className="flex-1 overflow-auto pt-16 md:pt-0">
            <AppRouter 
              currentView={navigation.currentView}
              navigation={navigation}
              appData={appData}
              userProfile={auth.userProfile}
              user={auth.user}
              modalActions={modalActions}
              crudActions={crudActions}
            />
          </main>
        </div>

        {/* Modals */}
        {modalActions.showCustomerSearchModal && (
          <CustomerSearchModal
            onClose={modalActions.closeCustomerSearchModal}
            onCustomerSelect={handleCustomerSearchSelect}
            onAddNew={handleCustomerSearchAddNew}
            user={auth.user}
          />
        )}

        {modalActions.showAddCustomerModal && (
          <AddCustomerModal
            onClose={() => {
              modalActions.closeAddCustomerModal();
              setJobCustomerCallback(false);
            }}
            onSubmit={
              treatmentReportCustomerCallback
                ? handleAddCustomerFromTreatmentReport
                : jobCustomerCallback
                  ? handleAddCustomerFromJobFlow
                  : crudActions.addCustomer
            }
            userProfile={auth.userProfile}
          />
        )}


        {modalActions.showAddJobModal && (
          <AddJobModal
            customers={appData.customers}
            onClose={modalActions.closeAddJobModal}
            onSubmit={modalActions.editingJob ? (data) => crudActions.updateJob(modalActions.editingJob.id, { ...data, franchisee_id: modalActions.editingJob.franchisee_id }) : crudActions.addJob}
            user={auth.user}
            job={modalActions.editingJob}
            isEditing={!!modalActions.editingJob}
            preSelectedCustomer={modalActions.preSelectedCustomer}
            mode={modalActions.editingJob?.category_path?.startsWith('Contract Services') ? 'contract' : modalActions.addJobMode}
          />
        )}

        {modalActions.showTreatmentReportEditor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) modalActions.closeTreatmentReportEditor(); }}>
            <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-auto">
              <TreatmentReportEditor
                report={modalActions.editingTreatmentReport}
                onSave={handleTreatmentReportSave}
                onCancel={modalActions.closeTreatmentReportEditor}
                userProfile={auth.userProfile}
                onOpenAddCustomerModal={handleOpenAddCustomerFromTreatmentReport}
              />
            </div>
          </div>
        )}

        {modalActions.showAddAppointmentModal && (
          <AddAppointmentModal
            onClose={modalActions.closeAddAppointmentModal}
            onSubmit={crudActions.addAppointment}
            user={auth.user}
            initialDateTime={modalActions.appointmentInitialData?.initialDateTime}
            initialData={modalActions.appointmentInitialData}
          />
        )}

        {modalActions.showEditAppointmentModal && (
          <EditAppointmentModal
            appointment={modalActions.editingAppointment}
            onClose={modalActions.closeEditAppointmentModal}
            onUpdate={(updateData) => crudActions.updateAppointment(modalActions.editingAppointment.id, { ...updateData, franchisee_id: modalActions.editingAppointment.franchisee_id })}
            onDelete={crudActions.deleteAppointment}
            onViewJob={(apt) => navigation.handleNavigation(VIEWS.JOB_DETAILS, { id: apt.job_id, customer_id: apt.customer_id, customer_name: apt.customer_name })}
            onViewCustomer={(apt) => navigation.handleNavigation(VIEWS.CUSTOMER_DETAILS, { id: apt.customer_id, name: apt.customer_name })}
          />
        )}
      </MobileHeaderProvider>
      </ErrorBoundary>
    </AuthGuard>
  );
}

export default App;